export interface WsVisibilityCallbacks {
    onHidden: () => void;
    onVisible: () => void;
}

export interface WsVisibilityHandle {
    detach: () => void;
    isHiddenGracePending: () => boolean;
}

export interface WsVisibilityOptions {
    gracePeriodMs?: number;
}

export const HIDDEN_TEARDOWN_GRACE_MS = 30_000;

export function attachWsVisibility(
    callbacks: WsVisibilityCallbacks,
    options: WsVisibilityOptions = {},
): WsVisibilityHandle {
    if (typeof document === 'undefined') {
        return { detach() {}, isHiddenGracePending: () => false };
    }

    let hiddenGraceTimerId: ReturnType<typeof setTimeout> | null = null;

    function clearHiddenGraceTimer(): void {
        if (hiddenGraceTimerId === null) return;

        clearTimeout(hiddenGraceTimerId);
        hiddenGraceTimerId = null;
    }

    const handler = () => {
        if (document.visibilityState === 'hidden') {
            if (options.gracePeriodMs && options.gracePeriodMs > 0) {
                if (hiddenGraceTimerId !== null) return;
                hiddenGraceTimerId = setTimeout(() => {
                    hiddenGraceTimerId = null;
                    if (document.visibilityState === 'hidden') {
                        callbacks.onHidden();
                    }
                }, options.gracePeriodMs);
                return;
            }

            callbacks.onHidden();
        } else {
            clearHiddenGraceTimer();
            callbacks.onVisible();
        }
    };

    document.addEventListener('visibilitychange', handler);

    let detached = false;
    return {
        detach() {
            if (detached) return;
            detached = true;
            clearHiddenGraceTimer();
            document.removeEventListener('visibilitychange', handler);
        },
        isHiddenGracePending() {
            return hiddenGraceTimerId !== null;
        },
    };
}

export function isBackgroundTab(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}
